import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Users, Plus, ArrowRight, UserPlus, X, Home } from 'lucide-react';

const SharedExpensesPage = () => {
    const { currentUser } = useAuth();
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Stato per la modale di creazione
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    // Nuovi stati per conti multi-membro
    const [accountName, setAccountName] = useState('');
    const [searchEmail, setSearchEmail] = useState('');
    const [searchError, setSearchError] = useState('');
    const [selectedMembers, setSelectedMembers] = useState([]); // Utenti trovati o invitati

    const [showInviteSuccess, setShowInviteSuccess] = useState(false);

    useEffect(() => {
        if (!currentUser) return;
        fetchAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const accountsRef = collection(db, 'sharedAccounts');
            const q = query(
                accountsRef,
                where('members', 'array-contains', currentUser.uid)
            );
            const snapshot = await getDocs(q);

            const accountsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Ordine lato client
            accountsList.sort((a, b) => {
                const timeA = a.createdAt?.toMillis() || 0;
                const timeB = b.createdAt?.toMillis() || 0;
                return timeB - timeA;
            });

            setAccounts(accountsList);
        } catch (error) {
            console.error("Errore nel caricamento conti:", error);
        } finally {
            setLoading(false);
        }
    };

    const resetModal = () => {
        setIsCreateModalOpen(false);
        setAccountName('');
        setSearchEmail('');
        setSearchError('');
        setSelectedMembers([]);
        setShowInviteSuccess(false);
    };

    const handleSearchAndAddUser = async (e) => {
        e.preventDefault();
        setSearchError('');

        const emailToSearch = searchEmail.toLowerCase().trim();

        if (!emailToSearch) return;

        if (emailToSearch === currentUser.email.toLowerCase()) {
            setSearchError("Non puoi aggiungere te stesso, sei già incluso!");
            return;
        }

        if (selectedMembers.some(m => m.email.toLowerCase() === emailToSearch)) {
            setSearchError("Questo utente è già nella lista!");
            return;
        }

        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('email', '==', emailToSearch));
            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                // Utente non trovato, aggiungilo come "In attesa"
                setSelectedMembers(prev => [...prev, {
                    email: emailToSearch,
                    displayName: "In attesa di registrazione",
                    isPending: true,
                    // Usiamo una chiave temporanea
                    uid: `pending_${emailToSearch}`
                }]);
            } else {
                // Utente trovato
                const userData = snapshot.docs[0].data();
                setSelectedMembers(prev => [...prev, { ...userData, isPending: false }]);
            }
            setSearchEmail(''); // Svuota il campo di ricerca dopo aver aggiunto
        } catch (error) {
            setSearchError("Errore durante la ricerca.");
            console.error(error);
        }
    };

    const removeMember = (uidToRemove) => {
        setSelectedMembers(prev => prev.filter(m => m.uid !== uidToRemove));
    };

    const handleCreateAccount = async () => {
        if (!accountName.trim() || selectedMembers.length === 0) {
            alert("Inserisci un nome per il conto e aggiungi almeno un membro.");
            return;
        }

        try {
            // Prepara i dati del nuovo conto
            const membersList = [currentUser.uid];
            const pendingEmailsList = [];

            const memberDetailsMap = {
                [currentUser.uid]: {
                    displayName: currentUser.displayName,
                    email: currentUser.email,
                    photoURL: currentUser.photoURL || null,
                    isPending: false
                }
            };

            selectedMembers.forEach(member => {
                if (member.isPending) {
                    pendingEmailsList.push(member.email.toLowerCase());
                    // Per la retrocompatibilità del vecchio componente visuale
                    memberDetailsMap[`pending_${member.email.toLowerCase()}`] = {
                        displayName: "In attesa di registrazione",
                        email: member.email.toLowerCase(),
                        isPending: true
                    };
                } else {
                    membersList.push(member.uid);
                    memberDetailsMap[member.uid] = {
                        displayName: member.displayName,
                        email: member.email,
                        photoURL: member.photoURL || null,
                        isPending: false
                    };
                }
            });

            const newAccount = {
                name: accountName.trim(),
                members: membersList,
                pendingEmails: pendingEmailsList,
                memberDetails: memberDetailsMap,
                createdAt: serverTimestamp()
            };

            await addDoc(collection(db, 'sharedAccounts'), newAccount);

            if (pendingEmailsList.length > 0) {
                setShowInviteSuccess(true);
            } else {
                resetModal();
            }
            fetchAccounts(); // Ricarica la lista
        } catch (error) {
            console.error("Errore nella creazione del conto:", error);
            alert("Errore durante la creazione del conto condiviso.");
        }
    };

    const copyInviteText = () => {
        // Estrai le email pendenti se ci sono
        const text = `Ciao! Ti ho appena aggiunto al conto "${accountName}" per tracciare le nostre spese condivise su Shongjog.\n\n` +
            `Per vedere il conto e aggiungere le tue spese registrati da qui usando la tua email:\n` +
            `https://shongjog.it/tools\n\n` +
            `A presto!\n${currentUser.displayName}`;

        navigator.clipboard.writeText(text);
        alert("Messaggio copiato negli appunti! Inviagli questo messaggio su WhatsApp o via Email.");

        resetModal();
    };

    if (!currentUser) return <Navigate to="/" />;

    return (
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '80px 20px 40px', minHeight: '80vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', flexWrap: 'wrap', gap: '15px' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '28px', color: '#006a4e', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Users size={32} /> Spese Condivise
                    </h1>
                    <p style={{ margin: '5px 0 0', color: 'var(--text-secondary)' }}>Condividi l'affitto e le spese con coinquilini o amici.</p>
                </div>
                <button
                    onClick={() => setIsCreateModalOpen(true)}
                    style={{
                        backgroundColor: '#006a4e', color: '#ffffff', border: 'none',
                        padding: '12px 20px', borderRadius: '8px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold'
                    }}
                >
                    <Plus size={20} /> Nuovo Conto
                </button>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>Caricamento in corso...</div>
            ) : accounts.length === 0 ? (
                <div style={{
                    backgroundColor: 'var(--bg-secondary)', border: '1px dashed var(--border-color)', borderRadius: '12px',
                    padding: '60px 20px', textAlign: 'center'
                }}>
                    <Home size={48} color="#006a4e" style={{ opacity: 0.5, marginBottom: '16px' }} />
                    <h3 style={{ margin: '0 0 10px', color: 'var(--text-primary)' }}>Nessun conto condiviso</h3>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>Crea un gruppo per la tua casa o per il tuo viaggio.</p>
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        style={{
                            backgroundColor: 'var(--bg-primary)', color: '#006a4e', border: '2px solid #006a4e',
                            padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                        }}
                    >
                        Crea il tuo primo conto
                    </button>
                </div>
            ) : (
                <div style={{ display: 'grid', gap: '16px' }}>
                    {accounts.map(acc => {
                        // Nuovo formato con 'name', fallback al vecchio formato 'Conto con...'
                        let accountTitle = acc.name;
                        let subtitle = "";

                        // Per conti con la "nuova" logica N membri
                        const totalMembersCount = acc.members.length + (acc.pendingEmails ? acc.pendingEmails.length : (acc.pendingEmail ? 1 : 0));

                        if (accountTitle) {
                            subtitle = `${totalMembersCount} partecipanti`;
                        } else {
                            // Logica Legacy: trova chi è l'altro utente nel conto
                            let otherUser = null;

                            if (acc.members.length === 1 && acc.pendingEmail) {
                                otherUser = acc.memberDetails[`pending_${acc.pendingEmail}`];
                            } else {
                                const otherUserId = acc.members.find(id => id !== currentUser.uid);
                                if (otherUserId) {
                                    otherUser = acc.memberDetails[otherUserId];
                                }
                            }
                            accountTitle = `Conto con ${otherUser?.isPending ? otherUser.email : (otherUser?.displayName?.split(' ')[0] || 'Utente')}`;
                            subtitle = otherUser?.isPending ? 'In attesa di registrazione...' : otherUser?.email;
                        }

                        return (
                            <Link
                                to={`/shared-expenses/${acc.id}`}
                                key={acc.id}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    backgroundColor: 'var(--bg-primary)', padding: '20px', borderRadius: '12px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)', textDecoration: 'none', color: 'inherit',
                                    border: '1px solid var(--border-color)', transition: 'transform 0.2s, box-shadow 0.2s'
                                }}
                                onMouseOver={e => {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                }}
                                onMouseOut={e => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <div style={{
                                        width: '50px', height: '50px', borderRadius: '50%',
                                        backgroundColor: '#e2f5ee', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', color: '#006a4e', overflow: 'hidden'
                                    }}>
                                        {acc.name ? <Home size={24} /> : <Users size={24} />}
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-primary)', fontWeight: '600' }}>
                                            {accountTitle}
                                        </h3>
                                        <p style={{ margin: '4px 0 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                            {subtitle}
                                        </p>
                                    </div>
                                </div>
                                <ArrowRight color="#ccc" />
                            </Link>
                        );
                    })}
                </div>
            )}

            {/* Modal Creazione Nuovo Gruppo */}
            {isCreateModalOpen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', zIndex: 1100, padding: '20px', overflowY: 'auto'
                }}>
                    <div style={{
                        backgroundColor: 'var(--bg-primary)', borderRadius: '16px', padding: '30px',
                        width: '100%', maxWidth: '420px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                        maxHeight: '90vh', overflowY: 'auto'
                    }}>
                        {!showInviteSuccess ? (
                            <>
                                <h2 style={{ margin: '0 0 20px', color: 'var(--text-primary)', fontSize: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    Crea Nuovo Conto
                                    <button onClick={resetModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                                        <X size={20} />
                                    </button>
                                </h2>

                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 'bold' }}>
                                        Nome del Conto (es. "Casa Roma", "Viaggio Ibiza")
                                    </label>
                                    <input
                                        type="text"
                                        value={accountName}
                                        onChange={(e) => setAccountName(e.target.value)}
                                        placeholder="Nome conto..."
                                        style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '16px' }}
                                    />
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '14px', fontWeight: 'bold' }}>
                                        Aggiungi Membri (Email)
                                    </label>
                                    <form onSubmit={handleSearchAndAddUser} style={{ display: 'flex', gap: '8px' }}>
                                        <input
                                            type="email"
                                            value={searchEmail}
                                            onChange={(e) => setSearchEmail(e.target.value)}
                                            placeholder="mario@email.com"
                                            style={{
                                                flex: 1, padding: '10px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '15px'
                                            }}
                                        />
                                        <button type="submit" style={{
                                            backgroundColor: '#006a4e', color: 'white', border: 'none', borderRadius: '8px', padding: '0 16px', cursor: 'pointer'
                                        }}>
                                            <Plus size={20} />
                                        </button>
                                    </form>
                                    {searchError && <p style={{ color: '#d32f2f', fontSize: '13px', margin: '8px 0 0 0' }}>{searchError}</p>}
                                </div>

                                {/* Lista dei membri aggiunti */}
                                <div style={{ marginBottom: '24px' }}>
                                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: 'var(--text-secondary)' }}>Membri:</h4>
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <li style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#006a4e', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>
                                                Tu
                                            </div>
                                            <span style={{ fontSize: '14px', flex: 1 }}>{currentUser.displayName} (Tu)</span>
                                        </li>
                                        {selectedMembers.map(member => (
                                            <li key={member.uid} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
                                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: member.isPending ? '#ccc' : '#e2f5ee', color: member.isPending ? '#666' : '#006a4e', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                    {member.photoURL ? <img src={member.photoURL} alt="" style={{ width: '100%', height: '100%' }} /> : <UserPlus size={16} />}
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <span style={{ fontSize: '14px', display: 'block' }}>{member.displayName}</span>
                                                    {member.isPending && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Non registrato</span>}
                                                </div>
                                                <button onClick={() => removeMember(member.uid)} style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', padding: '4px' }}>
                                                    <X size={16} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <button
                                    onClick={handleCreateAccount}
                                    style={{
                                        width: '100%', backgroundColor: '#006a4e', color: '#ffffff', border: 'none',
                                        borderRadius: '8px', padding: '14px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px'
                                    }}
                                >
                                    Crea Conto
                                </button>

                            </>
                        ) : (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#e2f5ee', color: '#006a4e', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto' }}>
                                    <Users size={32} />
                                </div>
                                <h2 style={{ margin: '0 0 10px', color: 'var(--text-primary)', fontSize: '22px' }}>Conto Creato! 🎉</h2>
                                <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: 1.5 }}>
                                    Il conto "{accountName}" è pronto!<br /><br />
                                    Ci sono membri non registrati. Copia l'invito qui sotto e inviaglielo su <strong>WhatsApp</strong> per farli iscrivere!
                                </p>
                                <button
                                    onClick={copyInviteText}
                                    style={{
                                        width: '100%', backgroundColor: '#006a4e', color: '#ffffff', border: 'none',
                                        borderRadius: '8px', padding: '14px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', marginBottom: '12px'
                                    }}
                                >
                                    Copia Messaggio d'Invito
                                </button>
                                <button
                                    onClick={resetModal}
                                    style={{ backgroundColor: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '8px 16px', fontWeight: '500' }}
                                >
                                    Chiudi e Vai al Conto
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SharedExpensesPage;
